import { Search } from "lucide-react";
import { useMemo, useState } from "react";

import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

import { CLIENT_STAGES, LEAD_TEMPERATURES, labelOf } from "../gulaConstants";
import { fullName, type GulaClient } from "../gulaTypes";
import { agentName, type Agent } from "../useAgents";
import { SEGMENTS, hasValidEmail, segmentOf, type Segment } from "./email";

export interface RecipientFilters {
  stages: string[];
  temperature: string;
  zone: string;
  agent: string;
  query: string;
}

export const emptyFilters = (): RecipientFilters => ({
  stages: [],
  temperature: "",
  zone: "",
  agent: "",
  query: "",
});

const normalize = (text: string) =>
  text.toLowerCase().normalize("NFD").replace(/[̀-ͯ]/g, "");

/** Phase, temperature, zone, agent and search. */
export const matchesFilters = (
  client: GulaClient,
  filters: RecipientFilters,
) => {
  if (filters.stages.length && !filters.stages.includes(client.stage))
    return false;
  if (filters.temperature && client.lead_temperature !== filters.temperature)
    return false;
  if (filters.zone && client.zone !== filters.zone) return false;
  if (filters.agent === "none" && client.sales_id != null) return false;
  if (
    filters.agent &&
    filters.agent !== "none" &&
    String(client.sales_id) !== filters.agent
  )
    return false;
  if (filters.query) {
    const haystack = normalize(
      `${fullName(client)} ${client.email ?? ""} ${client.city ?? ""} ${client.phone ?? ""}`,
    );
    if (!haystack.includes(normalize(filters.query))) return false;
  }
  return true;
};

const Chip = ({
  active,
  onClick,
  children,
}: {
  active: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <button
    type="button"
    onClick={onClick}
    className={cn(
      "rounded-full border px-3 py-1 text-sm transition-colors",
      active
        ? "border-foreground bg-foreground text-background"
        : "hover:bg-muted",
    )}
  >
    {children}
  </button>
);

const FilterSelect = ({
  value,
  onChange,
  options,
  placeholder,
}: {
  value: string;
  onChange: (value: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}) => (
  <select
    value={value}
    onChange={(event) => onChange(event.target.value)}
    className="h-9 rounded-md border bg-background px-2 text-sm"
  >
    <option value="">{placeholder}</option>
    {options.map((option) => (
      <option key={option.value} value={option.value}>
        {option.label}
      </option>
    ))}
  </select>
);

/**
 * Who gets the email: customers or leads to convert, narrowed with filters,
 * and each person can be unticked. People without a valid email or who
 * unsubscribed are left out and counted.
 */
export const RecipientPicker = ({
  clients,
  unsubscribed,
  agents,
  segment,
  onSegmentChange,
  filters,
  onFiltersChange,
  excluded,
  onExcludedChange,
}: {
  clients: GulaClient[];
  unsubscribed: Set<string>;
  agents: Agent[];
  segment: Segment;
  onSegmentChange: (segment: Segment) => void;
  filters: RecipientFilters;
  onFiltersChange: (filters: RecipientFilters) => void;
  excluded: Set<string>;
  onExcludedChange: (excluded: Set<string>) => void;
}) => {
  const [showAll, setShowAll] = useState(false);
  const inSegment = useMemo(
    () => clients.filter((client) => segmentOf(client) === segment),
    [clients, segment],
  );
  const stageOptions = CLIENT_STAGES.filter((stage) =>
    inSegment.some((client) => client.stage === stage.value),
  );
  const zones = [
    ...new Set(inSegment.map((client) => client.zone).filter(Boolean)),
  ].sort() as string[];

  const matching = inSegment.filter((client) =>
    matchesFilters(client, filters),
  );
  const isBlocked = (client: GulaClient) =>
    !!client.email && unsubscribed.has(client.email.trim().toLowerCase());
  const reachable = matching.filter(
    (client) => hasValidEmail(client) && !isBlocked(client),
  );
  const noEmail = matching.filter((client) => !hasValidEmail(client)).length;
  const blocked = matching.filter(
    (client) => hasValidEmail(client) && isBlocked(client),
  ).length;
  const selected = reachable.filter(
    (client) => !excluded.has(String(client.id)),
  );
  const visible = showAll ? matching : matching.slice(0, 60);

  const set = (patch: Partial<RecipientFilters>) =>
    onFiltersChange({ ...filters, ...patch });
  const toggle = (id: string) => {
    const next = new Set(excluded);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    onExcludedChange(next);
  };
  const setAll = (checked: boolean) => {
    const next = new Set(excluded);
    for (const client of reachable) {
      if (checked) next.delete(String(client.id));
      else next.add(String(client.id));
    }
    onExcludedChange(next);
  };

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
        {SEGMENTS.map((item) => {
          const count = clients.filter(
            (client) =>
              segmentOf(client) === item.value &&
              hasValidEmail(client) &&
              !isBlocked(client),
          ).length;
          return (
            <button
              key={item.value}
              type="button"
              onClick={() => {
                onSegmentChange(item.value);
                onFiltersChange(emptyFilters());
                onExcludedChange(new Set());
              }}
              className={cn(
                "flex flex-col gap-1 rounded-2xl border p-4 text-left transition-colors",
                segment === item.value
                  ? "border-foreground bg-muted"
                  : "hover:bg-muted/60",
              )}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="text-base font-semibold">{item.label}</span>
                <span className="text-sm text-muted-foreground">
                  {count} con email
                </span>
              </span>
              <span className="text-xs text-muted-foreground">{item.hint}</span>
            </button>
          );
        })}
      </div>

      {stageOptions.length > 1 ? (
        <div className="flex flex-wrap items-center gap-1.5">
          <span className="mr-1 text-sm text-muted-foreground">Fase</span>
          <Chip
            active={!filters.stages.length}
            onClick={() => set({ stages: [] })}
          >
            Todas
          </Chip>
          {stageOptions.map((stage) => (
            <Chip
              key={stage.value}
              active={filters.stages.includes(stage.value)}
              onClick={() =>
                set({
                  stages: filters.stages.includes(stage.value)
                    ? filters.stages.filter((item) => item !== stage.value)
                    : [...filters.stages, stage.value],
                })
              }
            >
              {stage.label}
            </Chip>
          ))}
        </div>
      ) : null}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative">
          <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
          <Input
            value={filters.query}
            onChange={(event) => set({ query: event.target.value })}
            placeholder="Buscar nombre, email, ciudad…"
            className="w-60 pl-8"
          />
        </div>
        <FilterSelect
          value={filters.temperature}
          onChange={(temperature) => set({ temperature })}
          options={LEAD_TEMPERATURES}
          placeholder="Temperatura: todas"
        />
        <FilterSelect
          value={filters.zone}
          onChange={(zone) => set({ zone })}
          options={zones.map((zone) => ({ value: zone, label: zone }))}
          placeholder="Zona: todas"
        />
        <FilterSelect
          value={filters.agent}
          onChange={(agent) => set({ agent })}
          options={[
            { value: "none", label: "Sin coger" },
            ...agents.map((agent) => ({
              value: String(agent.id),
              label: agentName(agent),
            })),
          ]}
          placeholder="Agente: todos"
        />
      </div>

      <div className="flex flex-wrap items-center justify-between gap-2 rounded-xl bg-muted/50 px-4 py-3">
        <span className="text-sm">
          <strong className="text-lg">{selected.length}</strong>{" "}
          {selected.length === 1
            ? "persona recibirá el correo"
            : "personas recibirán el correo"}
          <span className="text-muted-foreground">
            {noEmail ? ` · ${noEmail} sin email válido` : ""}
            {blocked ? ` · ${blocked} dados de baja` : ""}
            {reachable.length - selected.length
              ? ` · ${reachable.length - selected.length} quitados a mano`
              : ""}
          </span>
        </span>
        <span className="flex gap-3 text-sm">
          <button
            type="button"
            className="hover:underline"
            onClick={() => setAll(true)}
          >
            Marcar todos
          </button>
          <button
            type="button"
            className="hover:underline"
            onClick={() => setAll(false)}
          >
            Desmarcar todos
          </button>
        </span>
      </div>

      <ul className="flex flex-col divide-y rounded-xl border">
        {visible.map((client) => {
          const id = String(client.id);
          const valid = hasValidEmail(client);
          const off = !valid || isBlocked(client);
          return (
            <li key={id}>
              <label
                className={cn(
                  "flex cursor-pointer items-center gap-3 px-3 py-2 text-sm hover:bg-muted/50",
                  off && "cursor-default opacity-50",
                )}
              >
                <Checkbox
                  checked={!off && !excluded.has(id)}
                  disabled={off}
                  onCheckedChange={() => toggle(id)}
                />
                <span className="min-w-0 flex-1">
                  <span className="block truncate font-medium">
                    {fullName(client)}
                  </span>
                  <span className="block truncate text-xs text-muted-foreground">
                    {client.email || "Sin email"}
                    {isBlocked(client) ? " · dado de baja" : ""}
                  </span>
                </span>
                <span className="hidden shrink-0 text-xs text-muted-foreground sm:block">
                  {labelOf(CLIENT_STAGES, client.stage)}
                  {client.zone ? ` · ${client.zone}` : ""}
                </span>
              </label>
            </li>
          );
        })}
        {!matching.length ? (
          <li className="px-3 py-6 text-center text-sm text-muted-foreground">
            Nadie cumple estos filtros.
          </li>
        ) : null}
      </ul>
      {matching.length > visible.length ? (
        <button
          type="button"
          className="text-sm hover:underline"
          onClick={() => setShowAll(true)}
        >
          Ver los {matching.length} (se muestran 60)
        </button>
      ) : null}
    </div>
  );
};

/** The ids that will receive the email with these settings. */
export const selectedRecipientIds = (
  clients: GulaClient[],
  unsubscribed: Set<string>,
  segment: Segment,
  filters: RecipientFilters,
  excluded: Set<string>,
) =>
  clients
    .filter(
      (client) =>
        segmentOf(client) === segment &&
        matchesFilters(client, filters) &&
        hasValidEmail(client) &&
        !unsubscribed.has(client.email!.trim().toLowerCase()) &&
        !excluded.has(String(client.id)),
    )
    .map((client) => client.id);
